/**
 * IXVerify v2 — Studies page (ixv2 WP-2.3; plan §5.4 + Appendix A).
 *
 * Study management for IXVerify: protocol list with learning-profile status,
 * CRIO study id display/edit (§3.4 auto-sync mapping), registry-link chip
 * (rendered from study data only — the v1 registry-link endpoints were
 * deleted in WP-0.2; gated link routes return in WP-3.1), profile reset
 * (re-teach), and manual add. Ports the good parts of v1 StudyManagement,
 * rebuilt on the v2 shared primitives.
 *
 * Teaching (§5.4): there is no separate "teach" workflow — the CRC's ordinary
 * confirm/correct pass during dispensing IS the teaching. The UI therefore
 * shows teach state and offers RESET (re-teach); it never offers a fake
 * "teach now" button.
 *
 * Pilot feedback round 2 (owner requests, 2026-08-21):
 * - eReg AUTO-PRESENCE: `GET /studies` reconciles server-side, so every
 *   draft/active eReg protocol at the site simply appears here (and in the
 *   pickers) as an ordinary linked study — nothing to click.
 * - Study CLEANUP (admin): hard DELETE for studies with no signed records
 *   (typed-confirmation dialog stating exactly what is destroyed), ARCHIVE
 *   for everything else (reversible; archived studies collapse into their
 *   own admin-only section and disappear from every picker/filter, while
 *   signed history stays readable in History).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FlaskConical, Plus, GraduationCap, RotateCcw, Pencil, Link2,
  Archive, ArchiveRestore, Trash2, ChevronDown, ChevronRight, Loader2,
} from 'lucide-react';
import {
  PageHeader,
  RefreshButton,
  Modal,
  ConfirmModal,
  EmptyState,
  useToast,
  primaryBtnCls,
  secondaryBtnCls,
  outlineBtnCls,
  dangerBtnCls,
  fieldCls,
} from '../../ui';
import StudyPicker from '../../ui/StudyPicker';
import type { RegistryStudy } from '../../ui/StudyPicker';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrentSiteId } from '../../services/api';
import {
  listStudies,
  createStudy,
  updateStudy,
  linkStudyRegistry,
  unlinkStudyRegistry,
  resetStudyProfile,
  getStudyDeletePreview,
  deleteStudy,
  archiveStudy,
  unarchiveStudy,
  is501,
  errorMessage,
  IxvStudy,
  StudyDeletePreview,
} from '../lib/api';
import { RegistryLinkChip } from '../components/shared/badges';
import StubNotice from '../components/shared/StubNotice';

const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';

/** Learning-profile status pill (taught = a profile exists with ≥1 revision). */
function ProfileStatusPill({ study }: { study: IxvStudy }) {
  const taught = study.profile != null;
  return taught ? (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
      <GraduationCap className="h-3 w-3" aria-hidden="true" />
      Taught
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
      <GraduationCap className="h-3 w-3" aria-hidden="true" />
      Not yet taught
    </span>
  );
}

export default function Studies() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const canManage = isAdmin || isSuperAdmin;

  const [studies, setStudies] = useState<IxvStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [stubbed, setStubbed] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Add-study modal (eReg-first: pick a registry study or create it in eReg)
  const [addOpen, setAddOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSaving, setAddSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const handledCreatedRef = useRef(false);
  const siteId = getCurrentSiteId();

  // Registry link/unlink busy marker (per study)
  const [linkBusyId, setLinkBusyId] = useState<string | null>(null);

  // Edit modal (protocol name / sponsor / crio_study_id)
  const [editStudy, setEditStudy] = useState<IxvStudy | null>(null);
  const [editForm, setEditForm] = useState({ protocol_name: '', sponsor_name: '', crio_study_id: '' });
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Profile reset (re-teach) confirmation
  const [resetTarget, setResetTarget] = useState<IxvStudy | null>(null);
  const [resetBusy, setResetBusy] = useState(false);

  // Manual registry-link picker (for unlinked rows with no exact-match suggestion)
  const [linkTarget, setLinkTarget] = useState<IxvStudy | null>(null);

  // Pilot r2 — study cleanup state
  const [archiveTarget, setArchiveTarget] = useState<IxvStudy | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [unarchiveBusyId, setUnarchiveBusyId] = useState<string | null>(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IxvStudy | null>(null);
  const [deletePreview, setDeletePreview] = useState<StudyDeletePreview | null>(null);
  const [deletePreviewError, setDeletePreviewError] = useState<string | null>(null);
  const [deleteText, setDeleteText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  const fetchStudies = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const list = await listStudies();
      setStudies(list);
      setStubbed(false);
    } catch (err) {
      setStudies([]);
      if (is501(err)) {
        setStubbed(true);
      } else {
        setListError(errorMessage(err, 'Failed to load studies'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudies();
  }, [fetchStudies]);

  // Archived studies collapse into their own admin section and leave the
  // default list; the reconcile-driven auto-presence rows land in `active`.
  const activeStudies = useMemo(() => studies.filter((s) => !s.archived_at), [studies]);
  const archivedStudies = useMemo(() => studies.filter((s) => s.archived_at), [studies]);

  const handleAdd = useCallback(async (registryProtocolId: string) => {
    setAddSaving(true);
    setAddError(null);
    try {
      const study = await createStudy({ registry_protocol_id: registryProtocolId });
      setAddOpen(false);
      if (study?.archived_at) {
        toast(`${study.protocol_id} already exists but is archived — unarchive it under Archived studies.`);
      } else {
        toast('Study added');
      }
      fetchStudies();
    } catch (err) {
      setAddError(
        is501(err)
          ? 'Adding studies is not available yet — study management ships in a later IXVerify update.'
          : errorMessage(err, 'Failed to add study')
      );
      setAddOpen(true); // surface the error in the modal
    } finally {
      setAddSaving(false);
    }
  }, [toast, fetchStudies]);

  // Return leg from "Create new study in eReg": add + link the fresh study.
  useEffect(() => {
    const createdId = searchParams.get('created_protocol_id');
    if (!createdId || handledCreatedRef.current) return;
    handledCreatedRef.current = true;
    setSearchParams({}, { replace: true });
    handleAdd(createdId);
  }, [searchParams, setSearchParams, handleAdd]);

  const handleLink = async (study: IxvStudy, registryProtocolId: string) => {
    setLinkBusyId(study.id);
    try {
      await linkStudyRegistry(study.id, registryProtocolId);
      toast(`${study.protocol_id} linked to the registry`);
      fetchStudies();
    } catch (err) {
      toast(errorMessage(err, 'Failed to link study to the registry'), { type: 'error' });
    } finally {
      setLinkBusyId(null);
    }
  };

  const handleUnlink = async (study: IxvStudy) => {
    setLinkBusyId(study.id);
    try {
      await unlinkStudyRegistry(study.id);
      toast(`${study.protocol_id} unlinked from the registry`);
      fetchStudies();
    } catch (err) {
      toast(errorMessage(err, 'Failed to unlink study from the registry'), { type: 'error' });
    } finally {
      setLinkBusyId(null);
    }
  };

  const openEdit = (study: IxvStudy) => {
    setEditStudy(study);
    setEditForm({
      protocol_name: study.protocol_name ?? '',
      sponsor_name: study.sponsor_name ?? '',
      crio_study_id: study.crio_study_id ?? '',
    });
    setEditError(null);
  };

  const handleEdit = async () => {
    if (!editStudy) return;
    setEditSaving(true);
    setEditError(null);
    try {
      await updateStudy(editStudy.id, {
        protocol_name: editForm.protocol_name.trim() || undefined,
        sponsor_name: editForm.sponsor_name.trim() || undefined,
        // Explicit null clears the CRIO mapping; blank string never rides the wire.
        crio_study_id: editForm.crio_study_id.trim() || null,
      });
      setEditStudy(null);
      toast('Study updated');
      fetchStudies();
    } catch (err) {
      setEditError(
        is501(err)
          ? 'Editing studies is not available yet — study management ships in a later IXVerify update.'
          : errorMessage(err, 'Failed to update study')
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleReset = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      await resetStudyProfile(resetTarget.id);
      toast(`Learning profile reset for ${resetTarget.protocol_id}`);
      setResetTarget(null);
      fetchStudies();
    } catch (err) {
      toast(
        is501(err)
          ? 'Profile reset is not available yet.'
          : errorMessage(err, 'Failed to reset the learning profile'),
        { type: 'error' }
      );
      setResetTarget(null);
    } finally {
      setResetBusy(false);
    }
  };

  // --- Pilot r2: archive / unarchive / delete ------------------------------

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiveBusy(true);
    try {
      await archiveStudy(archiveTarget.id);
      toast(`${archiveTarget.protocol_id} archived`);
      setArchiveTarget(null);
      fetchStudies();
    } catch (err) {
      toast(errorMessage(err, 'Failed to archive study'), { type: 'error' });
      setArchiveTarget(null);
    } finally {
      setArchiveBusy(false);
    }
  };

  const handleUnarchive = async (study: IxvStudy) => {
    setUnarchiveBusyId(study.id);
    try {
      await unarchiveStudy(study.id);
      toast(`${study.protocol_id} unarchived`);
      fetchStudies();
    } catch (err) {
      toast(errorMessage(err, 'Failed to unarchive study'), { type: 'error' });
    } finally {
      setUnarchiveBusyId(null);
    }
  };

  const openDelete = async (study: IxvStudy) => {
    setDeleteTarget(study);
    setDeletePreview(null);
    setDeletePreviewError(null);
    setDeleteText('');
    try {
      setDeletePreview(await getStudyDeletePreview(study.id));
    } catch (err) {
      setDeletePreviewError(errorMessage(err, 'Failed to check what deleting this study would remove'));
    }
  };

  const closeDelete = () => {
    if (deleteBusy) return;
    setDeleteTarget(null);
    setDeletePreview(null);
    setDeletePreviewError(null);
    setDeleteText('');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteStudy(deleteTarget.id);
      toast(`${deleteTarget.protocol_id} deleted`);
      setDeleteTarget(null);
      setDeletePreview(null);
      setDeleteText('');
      fetchStudies();
    } catch (err) {
      const resp = (err as any)?.response;
      if (resp?.status === 409 && resp?.data?.code === 'IXV_STUDY_HAS_SIGNED_RECORDS') {
        // Raced a signature: flip the dialog into the blocked state.
        setDeletePreview((prev) => prev && {
          ...prev,
          eligible: false,
          signed_sessions: resp.data.signed_sessions ?? prev.signed_sessions,
          signed_assets: resp.data.signed_assets ?? prev.signed_assets,
        });
        toast('This study now contains signed records and cannot be deleted — archive it instead.', { type: 'error' });
      } else {
        toast(errorMessage(err, 'Failed to delete study'), { type: 'error' });
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  // Archive straight from the blocked delete dialog.
  const handleArchiveInstead = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await archiveStudy(deleteTarget.id);
      toast(`${deleteTarget.protocol_id} archived`);
      closeDeleteAfterAction();
      fetchStudies();
    } catch (err) {
      toast(errorMessage(err, 'Failed to archive study'), { type: 'error' });
    } finally {
      setDeleteBusy(false);
    }
  };

  const closeDeleteAfterAction = () => {
    setDeleteTarget(null);
    setDeletePreview(null);
    setDeletePreviewError(null);
    setDeleteText('');
  };

  const deleteConfirmed = deleteTarget != null && deleteText.trim() === deleteTarget.protocol_id;

  const renderAdminActions = (study: IxvStudy) => (
    <div className="flex flex-wrap gap-2">
      {study.registry_protocol_id ? (
        <button
          type="button"
          className={outlineBtnCls()}
          onClick={() => handleUnlink(study)}
          disabled={linkBusyId === study.id}
        >
          <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
          {linkBusyId === study.id ? 'Unlinking…' : 'Unlink registry'}
        </button>
      ) : study.registry_suggestion ? (
        <button
          type="button"
          className={primaryBtnCls}
          onClick={() => handleLink(study, study.registry_suggestion!.id)}
          disabled={linkBusyId === study.id}
        >
          <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
          {linkBusyId === study.id ? 'Linking…' : `Link to ${study.registry_suggestion.protocol_number}`}
        </button>
      ) : (
        <button
          type="button"
          className={outlineBtnCls()}
          onClick={() => setLinkTarget(study)}
          disabled={linkBusyId === study.id}
        >
          <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
          Link to registry
        </button>
      )}
      <button type="button" className={outlineBtnCls()} onClick={() => openEdit(study)}>
        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        Edit
      </button>
      {study.profile != null && (
        <button type="button" className={outlineBtnCls()} onClick={() => setResetTarget(study)}>
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Reset profile
        </button>
      )}
      <button type="button" className={outlineBtnCls()} onClick={() => setArchiveTarget(study)}>
        <Archive className="h-3.5 w-3.5" aria-hidden="true" />
        Archive
      </button>
      <button
        type="button"
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-1.5 text-sm font-medium text-red-600 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 sm:min-h-0"
        onClick={() => openDelete(study)}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
        Delete
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Studies"
        icon={FlaskConical}
        count={loading || stubbed ? null : activeStudies.length}
        subtitle="Protocols, learning profiles, and CRIO mapping"
        actions={
          <>
            <RefreshButton onClick={fetchStudies} loading={loading} />
            {canManage && (
              <button type="button" className={primaryBtnCls} onClick={() => setAddOpen(true)} disabled={stubbed}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add study
              </button>
            )}
          </>
        }
      />

      {loading ? (
        <div className="flex justify-center py-12" role="status" aria-label="Loading studies">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400" />
        </div>
      ) : stubbed ? (
        <StubNotice
          what="Study management"
          hint="IXVerify's study service hasn't shipped yet. Once it does, this page lists your protocols with their learning-profile status, CRIO mapping, and registry links."
        />
      ) : listError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <p className="text-sm text-red-700 dark:text-red-400">{listError}</p>
          <button type="button" className={`${outlineBtnCls()} mt-3`} onClick={fetchStudies}>
            Try again
          </button>
        </div>
      ) : activeStudies.length === 0 ? (
        <EmptyState
          variant="card"
          icon={FlaskConical}
          title="No studies yet"
          hint="Draft and active eReg studies appear here automatically. IXVerify learns each protocol's documents during the first dispensing sessions."
          action={
            canManage && (
              <button type="button" className={primaryBtnCls} onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add study
              </button>
            )
          }
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          {activeStudies.map((study) => (
            <li
              key={study.id}
              className={`rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 ${
                study.is_active === false ? 'opacity-60' : ''
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{study.protocol_id}</span>
                    <ProfileStatusPill study={study} />
                    {study.is_active === false && (
                      <span className="inline-flex items-center whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Inactive
                      </span>
                    )}
                    <RegistryLinkChip
                      registryProtocolId={study.registry_protocol_id}
                      registryProtocol={study.registry_protocol}
                    />
                  </div>
                  {(study.protocol_name || study.sponsor_name) && (
                    <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">
                      {study.protocol_name}
                      {study.protocol_name && study.sponsor_name && ' — '}
                      {study.sponsor_name}
                    </p>
                  )}
                </div>
                {canManage && renderAdminActions(study)}
              </div>

              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">CRIO study</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    {study.crio_study_id || <span className="text-gray-400 dark:text-gray-500">Not mapped</span>}
                  </dd>
                </div>
                {study.profile != null && (
                  <>
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Exemplars</dt>
                      <dd className="tabular-nums text-gray-900 dark:text-gray-100">{study.profile.exemplar_count ?? 0}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Typical kits</dt>
                      <dd className="tabular-nums text-gray-900 dark:text-gray-100">{study.profile.typical_kit_count ?? '—'}</dd>
                    </div>
                  </>
                )}
              </dl>

              {study.profile?.reading_legend != null && (
                /* Pilot r4: what IXVerify learned about reading this study's
                   documents — the concrete output of teaching mode. */
                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5 dark:border-gray-700 dark:bg-gray-900/40" data-testid="reading-legend">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Reading guide (learned)
                  </p>
                  {study.profile.reading_legend.kit_number_guide && (
                    <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Kit numbers:</span> {study.profile.reading_legend.kit_number_guide}
                    </p>
                  )}
                  {study.profile.reading_legend.document_guide && (
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{study.profile.reading_legend.document_guide}</p>
                  )}
                  {(study.profile.reading_legend.distractors?.length ?? 0) > 0 && (
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Never kit numbers:</span>{' '}
                      {study.profile.reading_legend.distractors!.join('; ')}
                    </p>
                  )}
                </div>
              )}

              {study.profile == null && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  IXVerify teaches itself during the first dispensing sessions for this protocol:
                  the CRC confirms (or types over) the numbers, and IXVerify writes itself a reading
                  guide for the study&apos;s documents — no separate setup needed.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* ---- Archived studies (admin-only, collapsed by default) ---- */}
      {!loading && !stubbed && !listError && canManage && archivedStudies.length > 0 && (
        <div>
          <button
            type="button"
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200 sm:min-h-0"
            onClick={() => setArchivedOpen((v) => !v)}
            aria-expanded={archivedOpen}
          >
            {archivedOpen ? (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            )}
            <Archive className="h-4 w-4" aria-hidden="true" />
            Archived studies ({archivedStudies.length})
          </button>
          {archivedOpen && (
            <ul className="mt-2 grid grid-cols-1 gap-3 xl:grid-cols-2">
              {archivedStudies.map((study) => (
                <li
                  key={study.id}
                  className="rounded-xl border border-gray-200 bg-white p-4 opacity-70 dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{study.protocol_id}</span>
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          <Archive className="h-3 w-3" aria-hidden="true" />
                          Archived
                        </span>
                        <RegistryLinkChip
                          registryProtocolId={study.registry_protocol_id}
                          registryProtocol={study.registry_protocol}
                        />
                      </div>
                      {(study.protocol_name || study.sponsor_name) && (
                        <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">
                          {study.protocol_name}
                          {study.protocol_name && study.sponsor_name && ' — '}
                          {study.sponsor_name}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className={outlineBtnCls()}
                        onClick={() => handleUnarchive(study)}
                        disabled={unarchiveBusyId === study.id}
                      >
                        <ArchiveRestore className="h-3.5 w-3.5" aria-hidden="true" />
                        {unarchiveBusyId === study.id ? 'Unarchiving…' : 'Unarchive'}
                      </button>
                      <button
                        type="button"
                        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-1.5 text-sm font-medium text-red-600 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 sm:min-h-0"
                        onClick={() => openDelete(study)}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ---- Add study (eReg-first: pick a registry study or create it in eReg) ---- */}
      <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} title="Add study" maxWidth="max-w-md">
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Studies are created in eReg — the platform study registry. Draft and active registry
            studies appear here automatically; pick one below, or create it in eReg and it returns
            here automatically.
          </p>
          {addError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-700 dark:text-red-400">{addError}</p>
            </div>
          )}
          <StudyPicker
            siteId={siteId || undefined}
            onSelect={(registryStudy: RegistryStudy) => handleAdd(registryStudy.id)}
            createReturnTo="/ixverify/studies"
            createReturnLabel="IXVerify"
            placeholder="Search the registry by protocol number, name, or sponsor…"
            autoFocus
          />
          <div className="flex items-center justify-end gap-2">
            {addSaving && <span className="text-sm text-gray-500 dark:text-gray-400">Adding…</span>}
            <button type="button" className={secondaryBtnCls} onClick={() => setAddOpen(false)}>Cancel</button>
          </div>
        </div>
      </Modal>

      {/* ---- Link an existing study to the registry (no exact match found) ---- */}
      <Modal
        isOpen={linkTarget != null}
        onClose={() => setLinkTarget(null)}
        title={`Link ${linkTarget?.protocol_id ?? 'study'} to the registry`}
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Connect this study to its eReg registry record. If the study doesn't exist in eReg
            yet, create it there first.
          </p>
          <StudyPicker
            siteId={siteId || undefined}
            onSelect={(registryStudy: RegistryStudy) => {
              const target = linkTarget;
              setLinkTarget(null);
              if (target) handleLink(target, registryStudy.id);
            }}
            allowCreate={false}
            placeholder="Search the registry by protocol number, name, or sponsor…"
            autoFocus
          />
          <div className="flex justify-end">
            <button type="button" className={secondaryBtnCls} onClick={() => setLinkTarget(null)}>Cancel</button>
          </div>
        </div>
      </Modal>

      {/* ---- Edit study (name / sponsor / crio_study_id) ---- */}
      <Modal isOpen={editStudy != null} onClose={() => setEditStudy(null)} title={`Edit ${editStudy?.protocol_id ?? 'study'}`} maxWidth="max-w-md">
        <div className="space-y-4">
          {editError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-700 dark:text-red-400">{editError}</p>
            </div>
          )}
          <div>
            <label htmlFor="ixv-edit-name" className={labelCls}>Protocol name</label>
            <input
              id="ixv-edit-name"
              className={`${fieldCls} w-full`}
              value={editForm.protocol_name}
              onChange={(e) => setEditForm((f) => ({ ...f, protocol_name: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="ixv-edit-sponsor" className={labelCls}>Sponsor</label>
            <input
              id="ixv-edit-sponsor"
              className={`${fieldCls} w-full`}
              value={editForm.sponsor_name}
              onChange={(e) => setEditForm((f) => ({ ...f, sponsor_name: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="ixv-edit-crio" className={labelCls}>CRIO study ID</label>
            <input
              id="ixv-edit-crio"
              className={`${fieldCls} w-full`}
              value={editForm.crio_study_id}
              onChange={(e) => setEditForm((f) => ({ ...f, crio_study_id: e.target.value }))}
              placeholder="Ties this protocol to its CRIO study"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              With a CRIO study mapped, subjects sync onto this roster automatically. Leave blank
              for non-CRIO studies.
            </p>
            {editStudy?.registry_protocol_id && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This study is linked to eReg, so the CRIO ID is shared: saving it here also records
                it on the eReg study and its CRM record.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className={secondaryBtnCls} onClick={() => setEditStudy(null)}>Cancel</button>
            <button type="button" className={primaryBtnCls} onClick={handleEdit} disabled={editSaving}>
              {editSaving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ---- Reset learning profile (re-teach) ---- */}
      <ConfirmModal
        isOpen={resetTarget != null}
        onClose={() => setResetTarget(null)}
        onConfirm={handleReset}
        loading={resetBusy}
        danger
        title="Reset learning profile"
        confirmLabel="Reset profile"
        message={
          resetTarget && (
            <>
              Reset everything IXVerify has learned about{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">{resetTarget.protocol_id}</span>?
              The next dispensing sessions will re-teach it from scratch. A snapshot of the current
              profile is kept for rollback.
            </>
          )
        }
      />

      {/* ---- Archive study (pilot r2 — reversible) ---- */}
      <ConfirmModal
        isOpen={archiveTarget != null}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
        loading={archiveBusy}
        title="Archive study"
        confirmLabel="Archive study"
        message={
          archiveTarget && (
            <>
              Archive{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">{archiveTarget.protocol_id}</span>?
              It disappears from study lists and pickers, but its verification history stays
              readable. You can unarchive it any time.
            </>
          )
        }
      />

      {/* ---- Delete study (pilot r2 — typed confirmation, or blocked → archive) ---- */}
      <Modal
        isOpen={deleteTarget != null}
        onClose={closeDelete}
        title={`Delete ${deleteTarget?.protocol_id ?? 'study'}`}
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          {deletePreviewError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-700 dark:text-red-400">{deletePreviewError}</p>
            </div>
          ) : deletePreview == null ? (
            <div className="flex items-center gap-2 py-4 text-sm text-gray-500 dark:text-gray-400" role="status">
              <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
              Checking what deleting this study would remove…
            </div>
          ) : deletePreview.eligible ? (
            <>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  This permanently destroys everything IXVerify holds for this study:
                </p>
                <ul className="mt-2 list-inside list-disc text-sm text-red-700 dark:text-red-400">
                  <li>{deletePreview.counts.sessions} verification session{deletePreview.counts.sessions === 1 ? '' : 's'}</li>
                  <li>{deletePreview.counts.subjects} roster subject{deletePreview.counts.subjects === 1 ? '' : 's'}</li>
                  <li>{deletePreview.counts.assets} captured photo{deletePreview.counts.assets === 1 ? '' : 's'}/document{deletePreview.counts.assets === 1 ? '' : 's'} — the stored files are deleted for real</li>
                </ul>
                <p className="mt-2 text-sm text-red-700 dark:text-red-400">This cannot be undone.</p>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The eReg study itself is not touched. While it stays draft or active in eReg, an
                empty copy of this study reappears here automatically — archive instead if you
                want it out of the lists.
              </p>
              <div>
                <label htmlFor="ixv-delete-confirm" className={labelCls}>
                  Type <span className="font-semibold normal-case">{deleteTarget?.protocol_id}</span> to confirm
                </label>
                <input
                  id="ixv-delete-confirm"
                  className={`${fieldCls} w-full`}
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className={secondaryBtnCls} onClick={closeDelete} disabled={deleteBusy}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={dangerBtnCls}
                  onClick={handleDelete}
                  disabled={!deleteConfirmed || deleteBusy}
                >
                  {deleteBusy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  Delete study
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  This study can't be deleted: it contains{' '}
                  <span className="font-medium">
                    {deletePreview.signed_sessions > 0
                      ? `${deletePreview.signed_sessions} signed session${deletePreview.signed_sessions === 1 ? '' : 's'}`
                      : `${deletePreview.signed_assets} signature-covered photo${deletePreview.signed_assets === 1 ? '' : 's'}`}
                  </span>
                  . Signed verification records are retained under 21 CFR Part 11.
                </p>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Archive it instead — it disappears from lists and pickers while its records stay
                readable in History.
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" className={secondaryBtnCls} onClick={closeDelete} disabled={deleteBusy}>
                  Cancel
                </button>
                {deleteTarget && !deleteTarget.archived_at && (
                  <button type="button" className={primaryBtnCls} onClick={handleArchiveInstead} disabled={deleteBusy}>
                    {deleteBusy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    <Archive className="h-4 w-4" aria-hidden="true" />
                    Archive instead
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
